import { useCallback, useEffect, useRef, useState } from "react";
// import { useDropzone } from "react-dropzone";
// import { Button } from "react-bootstrap";
import { LinearProgress } from "@mui/material";
// import { toast } from "react-toastify";

import { api_without_cancellation } from "../../../api/api";
import { getCaseId, getClientId } from "../../../Utils/helper";
import { notifyPageAttachmentsChanged } from "../../../Utils/pageAttachmentEvents";
import CaseUploadButton from "../../common/CaseUploadButton";

import NewDocumentTab from "./new-document-tab";
import "../../../Modules/new-doc.css";

const PAGE_SIZE = 50;

export const createDocsTab = (onClick) => ({
  id: "docs",
  label: "Docs",
  className: "tab-trapezium-cycles-corners",
  background: "var(--primary-60)",
  activeColor: "white",
  left: "0px",
  onClick,
  leftHand: true,
  span: (
    <span className="fw-bold ic-15 d-flex align-items-center">
      <img
        src="https://simplefirm-bucket.s3.amazonaws.com/static/images/documents-icon-color_b6TvcB7.svg"
        width={15}
        height={15}
        alt=""
      />
    </span>
  ),
});

/* Legacy drag/drop styles, retained with the commented upload UI below.
const styles = {
  uploadZone: {
    border: "2px dashed #d1d5db",
    backgroundColor: "#fafbfc",
    padding: "24px",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    minHeight: "100px",
    width: "100%",
  },
  uploadZoneActive: {
    borderColor: "var(--primary-50)",
    backgroundColor: "var(--primary-15)",
    borderStyle: "solid",
  },
  uploadButton: {
    minWidth: "160px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  hintText: { color: "#6b7280", fontSize: "0.9rem", margin: 0, pointerEvents: "none" },
  divider: { color: "#9ca3af", fontSize: "0.85rem", pointerEvents: "none" },
};

*/

// Same windowing as the Doc page pager: all pages up to 22, otherwise ellipses.
const pageNumbers = (totalPages, current) => {
  if (totalPages <= 22) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 7) return [1, 2, 3, 4, 5, 6, 7, 8, "...", totalPages];
  if (current >= totalPages - 3) {
    return [1, "...", ...Array.from({ length: 5 }, (_, i) => totalPages - 4 + i)];
  }
  const pages = [1];
  if (current - 1 > 2) pages.push("...");
  pages.push(current - 1, current, current + 1);
  if (current + 1 < totalPages - 1) pages.push("...");
  pages.push(totalPages);
  return pages;
};

const PageDocsTab = ({ pageId, pageName, pageIcon: _pageIcon, onAttachmentsChange }) => {
  const [data, setData] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  // const [isUploading, setIsUploading] = useState(false);
  const latestRequestId = useRef(0);

  const fetchDocs = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    try {
      const res = await api_without_cancellation.get("/api/doc-page/list-page-docs-centric/", {
        params: {
          case_id: getCaseId(),
          client_id: getClientId(),
          page_id: pageId,
          page: pageNo,
          page_size: PAGE_SIZE,
        },
      });
      if (requestId === latestRequestId.current) {
        setData(res.data);
        notifyPageAttachmentsChanged(getCaseId(), pageId, "docs");
        onAttachmentsChange?.();
      }
    } catch (error) {
      if (requestId === latestRequestId.current) console.error(error);
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, [pageId, pageNo, onAttachmentsChange]);

  useEffect(() => {
    fetchDocs();
    // Invalidate pending requests when the page changes or the tab unmounts.
    return () => {
      latestRequestId.current += 1;
    };
  }, [fetchDocs]);

  /* Legacy document-only upload handler, replaced by CaseUploadButton.
  const handleDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      setIsUploading(true);
      let failed = 0;
      for (const file of acceptedFiles) {
        try {
          const formData = new FormData();
          formData.append("file", file);
          const response = await api.post(
            `/api/upload_doc/${getClientId()}/${getCaseId()}/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          const docId = response.data?.docId;
          if (!docId) throw new Error("Upload returned no document id");
          // panels: false attaches the doc to the page generally
          await api.post("api/doc/attach/", {
            slot: "",
            page_id: pageId,
            case_id: getCaseId(),
            doc_id: docId,
            file_size: file.size / (1024 * 1024),
            panels: false,
          });
        } catch (error) {
          console.error(error);
          failed += 1;
        }
      }
      if (failed)
        toast.error(`${failed} of ${acceptedFiles.length} file(s) could not be uploaded.`);
      setIsUploading(false);
      if (pageNo === 1) fetchDocs();
      else setPageNo(1);
    },
    [pageId, pageNo, fetchDocs]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    noClick: true,
  });
  */

  const handleUploaded = () => {
    if (pageNo === 1) return fetchDocs();
    setPageNo(1);
  };

  const totalPages = data?.total_pages;
  const hasDocs = data?.results?.length > 0;

  return (
    <div className="w-100">
      <div
        style={{
          width: "100%",
          background: "var(--primary)",
          height: "25px",
          marginBottom: "5px",
          position: "relative",
        }}
      >
        <span
          className="fw-bold text-white"
          style={{
            height: "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {pageName || "Page"} Documents
        </span>
        <div
          className="d-flex align-items-center"
          style={{ position: "absolute", top: 0, right: 9, height: 25, gap: 10 }}
        >
          {totalPages > 0 && (
            <div style={{ height: "25px" }}>
              <div className="d-flex align-items-center">
                <span
                  style={{
                    color: "var(--primary-25)",
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  PAGE:
                </span>
                {pageNumbers(totalPages, pageNo).map((page, idx) =>
                  page === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-2" style={{ color: "white" }}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      style={{
                        color: page === pageNo ? "white" : "var(--primary-25)",
                        fontWeight: "600",
                        height: "25px",
                        width: "20px",
                      }}
                      className="bg-hover-page-nos p-0"
                      onClick={() => setPageNo(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              </div>
            </div>
          )}
          <CaseUploadButton pageId={pageId} pageName={pageName} onUploaded={handleUploaded} />
        </div>
      </div>
      {/* Legacy upload UI, replaced by the shared button in the primary bar.
      <div className="p-r-5 p-l-5">
        <div
          {...getRootProps()}
          className="m-b-5"
          style={{ ...styles.uploadZone, ...(isDragActive ? styles.uploadZoneActive : {}) }}
        >
          <input {...getInputProps()} />
          <Button
            variant="primary"
            style={styles.uploadButton}
            onClick={(e) => {
              e.stopPropagation();
              if (!isUploading) open();
            }}
            disabled={isUploading}
          >
            {isUploading ? (
              <span>Uploading...</span>
            ) : (
              <>
                <i className="fas fa-cloud-upload-alt" />
                <span>Choose Document</span>
              </>
            )}
          </Button>
          <span style={styles.divider}>or</span>
          <p style={styles.hintText}>
            {isDragActive ? (
              <strong>Drop the document to upload</strong>
            ) : (
              <>
                Drag and drop a document anywhere in this area to upload it to the{" "}
                {pageIcon && (
                  <img
                    src={pageIcon}
                    alt=""
                    style={{ width: "19px", height: "19px", verticalAlign: "middle" }}
                  />
                )}{" "}
                {pageName || "current page"}
              </>
            )}
          </p>
        </div>
      </div>
      */}

      {loading ? (
        <div style={{ padding: "5px", margin: "25px auto", color: "var(--primary)" }}>
          <LinearProgress color="inherit" />
        </div>
      ) : hasDocs ? (
        <NewDocumentTab data={data} refetchData={fetchDocs} />
      ) : (
        <p className="text-center m-t-5" style={{ color: "var(--primary-25)", fontWeight: 600 }}>
          {/* No documents attached to this page generally. */}
        </p>
      )}
    </div>
  );
};

export default PageDocsTab;
